import { AvailCheck } from "../../../pojo/AvailabilityCheck";
import { Fls } from "../../../pojo/Fls";
import { Item } from "../../../pojo/Item";
import { ResourceSpaceAvailService } from "../../../services/resource.space.avail.service";
import { SpaceService } from "../../../services/space.service";
import { S25Util } from "../../../util/s25-util";

type ConflictDefaults = AvailCheck.ConflictDefaults;

type AvailDateWindow = {
    startDt: Date;
    endDt: Date;
};

/** Micro/event error reasons that indicate a space (or similar) assignment conflict. */
const SPACE_CONFLICT_REASONS = new Set(["EV_E_SPACECON_TIME", "EV_E_SPACECON", "EV_W_SPACECON", "EV_I_SPACECON"]);

/**
 * "Space Mandy unavailable on Fri Aug 07 2026 due to [rsrv] conflict with SEE 08062026  A [436543]"
 */
const SPACE_CONFLICT_MESSAGE_PATTERN =
    /^Space (.+?) unavailable on (.+?) due to \[([^\]]+)\] conflict with (.+) \[(\d+)\]\s*$/i;

type MicroErrorMessage = {
    errorNum?: string;
    objectType?: number;
    objectId?: number;
    reason?: string;
    message?: string;
};

type MicroErrorPayload = {
    reason?: string;
    message?: string;
    messages?: MicroErrorMessage[];
    error?: {
        reason?: string;
        message?: string;
        messages?: MicroErrorMessage[];
    };
};

export namespace SpaceConflictErrorUtil {
    /** Modal copy when an avail check finds conflicts before save. */
    export const SAVE_BLOCKED_MESSAGE = "Cannot be saved due to conflicts. Please fix any conflicts before continuing.";

    /** Fallback when a space-conflict PUT error has no summary message. */
    export const DEFAULT_SUMMARY_MESSAGE = "Cannot change reservation time due to space conflict.";

    export function isSpaceConflictError(error: unknown): boolean {
        const payload = getPayload(error);
        if (!payload) {
            // Last-resort text match for odd wrappers / stringified bodies
            const text = S25Util.errorText(error) ?? getUnknownErrorMessage(error);
            return /space conflict/i.test(text) || /EV_[EWI]_SPACECON/i.test(text);
        }
        const reason = payload.reason ?? payload.error?.reason;
        if (reason && SPACE_CONFLICT_REASONS.has(reason)) return true;
        return getDetailMessages(payload).some((msg) => !!msg.reason && SPACE_CONFLICT_REASONS.has(msg.reason));
    }

    export function getSummaryMessage(error: unknown): string {
        const payload = getPayload(error);
        return payload?.message ?? payload?.error?.message ?? DEFAULT_SUMMARY_MESSAGE;
    }

    /**
     * ANG-7691: parse a micro PUT space-conflict error into summary + display rows.
     * Returns undefined when the error is not a space conflict.
     * Callers should open the modal with `conflicts` immediately, then optionally
     * `enrichWithAvailTimes` and refresh the list.
     */
    export function parsePutError(
        error: unknown,
        locationItems?: { itemId?: number; itemName?: string }[],
    ): { summary: string; conflicts: ConflictDefaults[] } | undefined {
        if (!isSpaceConflictError(error)) return undefined;
        return {
            summary: getSummaryMessage(error),
            conflicts: toDisplayConflicts(error, locationItems),
        };
    }

    /**
     * Convert micro PUT space-conflict detail messages into ConflictDefaults for s25-ng-conflicts.
     * Optional locationItems supplies itemId when the API message only includes the space name.
     * Note: messages are date-only — call enrichWithAvailTimes to fill conflict start/end like the
     * date/time edit conflict modal (avail API).
     */
    export function toDisplayConflicts(
        error: unknown,
        locationItems?: { itemId?: number; itemName?: string }[],
    ): ConflictDefaults[] {
        const payload = getPayload(error);
        if (!payload) return [];

        return getDetailMessages(payload)
            .filter((msg) => !!msg.message && (!msg.reason || SPACE_CONFLICT_REASONS.has(msg.reason)))
            .map((msg, index) => parseDetailMessage(msg.message!, index, locationItems))
            .filter((conflict): conflict is ConflictDefaults => !!conflict);
    }

    /**
     * Fill conflictStart/conflictEnd from space_avail so the modal shows times like date/time edit.
     * Falls back to the date-only rows when avail cannot be loaded or no match is found.
     */
    export async function enrichWithAvailTimes(
        conflicts: ConflictDefaults[],
        options: {
            dates?: AvailDateWindow[];
            eventId?: number;
            profileId?: number;
            fls?: Fls;
        } = {},
    ): Promise<ConflictDefaults[]> {
        if (!conflicts.length) return conflicts;

        const locationIds = S25Util.array.unique(
            conflicts.map((conflict) => conflict.itemId).filter((id) => !!id && Number.isFinite(id)),
        );
        if (!locationIds.length) return conflicts;

        const datesFromConflicts = conflicts.map((conflict) => ({
            startDt: S25Util.date.toStartOfDay(conflict.candidateDate),
            endDt: S25Util.date.toEndOfDay(conflict.candidateDate),
        }));
        const dates = options.dates?.length ? [...options.dates, ...datesFromConflicts] : datesFromConflicts;

        try {
            const avail = await SpaceService.getSpaceDatesAvailability(
                locationIds,
                dates,
                options.eventId,
                options.profileId,
            );
            const availConflicts = ResourceSpaceAvailService.toDisplayConflicts(avail, "space", options.fls);

            return conflicts.map((errorConflict) => {
                const match = availConflicts.find(
                    (availConflict) =>
                        availConflict.conflictId === errorConflict.conflictId &&
                        S25Util.date.equalDate(availConflict.candidateDate, errorConflict.candidateDate) &&
                        (!errorConflict.itemId ||
                            availConflict.itemId === errorConflict.itemId ||
                            availConflict.itemName?.toLowerCase() === errorConflict.itemName?.toLowerCase()),
                );
                if (!match?.conflictStart || !match?.conflictEnd) return errorConflict;

                return {
                    ...errorConflict,
                    candidateId: errorConflict.candidateId || match.candidateId,
                    conflictEnd: match.conflictEnd,
                    conflictStart: match.conflictStart,
                    itemId: errorConflict.itemId || match.itemId,
                };
            });
        } catch {
            return conflicts;
        }
    }

    function getUnknownErrorMessage(error: unknown): string {
        if (error && typeof error === "object" && "message" in error) {
            const message = (error as { message?: unknown }).message;
            if (typeof message === "string") return message;
        }
        return String(error ?? "");
    }

    function getPayload(error: unknown): MicroErrorPayload | undefined {
        if (!error || typeof error !== "object") return undefined;
        const root = error as Record<string, unknown>;

        // DataAccess sets `data`; Angular HttpErrorResponse uses `error` for the response body.
        // Walk common wrappers so EV_E_SPACECON_TIME is found regardless of which layer rejects.
        const candidates: unknown[] = [root.data, root.error, root.content, root];
        for (const candidate of candidates) {
            if (!candidate || typeof candidate !== "object") continue;
            const node = candidate as Record<string, unknown>;

            if (isConflictPayload(node)) return node as MicroErrorPayload;

            const content = node.content;
            if (content && typeof content === "object") {
                const contentNode = content as Record<string, unknown>;
                if (isConflictPayload(contentNode)) return contentNode as MicroErrorPayload;
                if (
                    contentNode.error &&
                    typeof contentNode.error === "object" &&
                    isConflictPayload(contentNode.error as Record<string, unknown>)
                ) {
                    return contentNode.error as MicroErrorPayload;
                }
            }

            if (
                node.error &&
                typeof node.error === "object" &&
                isConflictPayload(node.error as Record<string, unknown>)
            ) {
                return node.error as MicroErrorPayload;
            }
        }
        return undefined;
    }

    function isConflictPayload(node: Record<string, unknown>): boolean {
        if (typeof node.reason === "string" && SPACE_CONFLICT_REASONS.has(node.reason)) return true;
        const messages = S25Util.array.forceArray(node.messages) as MicroErrorMessage[];
        return messages.some(
            (msg) =>
                (!!msg?.reason && SPACE_CONFLICT_REASONS.has(msg.reason)) ||
                !!msg?.message?.match?.(SPACE_CONFLICT_MESSAGE_PATTERN),
        );
    }

    function getDetailMessages(payload: MicroErrorPayload): MicroErrorMessage[] {
        const nested = payload.error?.messages ?? payload.messages;
        return S25Util.array.forceArray(nested).filter((msg): msg is MicroErrorMessage => !!msg?.message);
    }

    function parseDetailMessage(
        message: string,
        index: number,
        locationItems?: { itemId?: number; itemName?: string }[],
    ): ConflictDefaults | undefined {
        const match = message.match(SPACE_CONFLICT_MESSAGE_PATTERN);
        if (!match) {
            // Fall back to a single non-linkable row so the raw API text is still visible
            return {
                candidateDate: new Date(),
                candidateId: 0,
                conflictEnd: new Date(),
                conflictHash: `raw-${index}-${message}`,
                conflictId: 0,
                conflictName: message,
                conflictOverride: { blackout: false, closed: false, block: false, share: false },
                conflictRelation: "direct",
                conflictShare: false,
                conflictStart: new Date(),
                conflictType: "rsrv",
                date: "",
                isReal: true,
                itemId: 0,
                itemName: "",
                itemTypeId: Item.Ids.Location,
                occUUID: 0,
                uuid: `raw-${index}-${message}`,
            };
        }

        const [, spaceName, dateText, conflictTypeRaw, eventName, eventIdText] = match;
        // API messages are date-only (e.g. "Fri Aug 07 2026") — keep local calendar day, no fake times
        const parsedDate = S25Util.date.parse(dateText);
        const candidateDate =
            parsedDate && !Number.isNaN(parsedDate.getTime())
                ? S25Util.date.toStartOfDay(parsedDate)
                : S25Util.date.toStartOfDay(new Date());
        const conflictId = Number(eventIdText);
        const conflictType = (
            ["rsrv", "block", "hours", "blackout", "req_rsrv", "req_block"].includes(conflictTypeRaw)
                ? conflictTypeRaw
                : "rsrv"
        ) as AvailCheck.conflictTypes;
        const uuid = `${spaceName}-${dateText}-${conflictId}-${index}`;
        const matchedLocation = locationItems?.find(
            (location) => location.itemName?.toLowerCase() === spaceName.trim().toLowerCase(),
        );

        return {
            candidateDate,
            candidateId: matchedLocation?.itemId ?? 0,
            // Same start/end signals date-only to s25-ng-conflicts (hides time span)
            conflictEnd: candidateDate,
            conflictHash: uuid,
            conflictId: Number.isFinite(conflictId) ? conflictId : 0,
            conflictName: eventName.trim(),
            conflictOverride: { blackout: false, closed: false, block: false, share: false },
            conflictRelation: "direct",
            conflictShare: false,
            conflictStart: candidateDate,
            conflictType,
            date: S25Util.date.toS25ISODateTimeStr(candidateDate) ?? "",
            isReal: true,
            itemId: matchedLocation?.itemId ?? 0,
            itemName: spaceName.trim(),
            itemTypeId: Item.Ids.Location,
            occUUID: 0,
            uuid,
        };
    }
}
